import React, {
  useCallback,
  forwardRef,
  ReactElement,
  Ref,
  useState,
  useEffect,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
//common components
import CustomDialog from "../../common/Dialog";
//redux
import {
  closeDialog,
  getAddressStatus,
  fetchSupportDriverDetails,
  releaseSupportDriver,
  releasePrimaryDriver,
  fetchClubbingSuggestion,
  clearClubbingSuggestion,
} from "../redux/expressSlice";
import { AppState } from "../../config/redux/reducers";
//material-ui components
import { TransitionProps } from "@material-ui/core/transitions";
import Slide from "@material-ui/core/Slide";
import {
  Grid,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
  Tab,
  Tabs,
  Tooltip,
} from "@material-ui/core";

import locationIcon from "../../assets/icons/map-pin.svg";
import unverifiedIcon from "../../assets/icons/unverified-icon.svg";
import infoIcon from "../../assets/icons/info-icon.svg";
import SupportDriver from "./SupportDriver";
//styles
import { useStyles } from "./DriverAssignment.styles";
import AppConstants from "../../constants";
import { getAddressInfo } from "../../mocks/SharedTransform/response.transforms";
//utils
import { localTimeString, getVehicleIcon, isNonEmptyObject } from "utils/helpers.utils";
import {
  createPayloadToMarkAddressUnverified,
} from "../../mocks/express/response.transforms";
import { hasValidClubbingSuggestions, isStoreEnabledInConfig } from  "utils/productUtils";
//constants
import { ADDRESS_STATUS } from "../../constants/CommonConstants";
import { TabContext, TabPanel } from "@material-ui/lab";

import {
  AssignedDriverChips,
} from "./DriverCommonComponent";
import PrimaryDriver from "./PrimaryDriver";
import { TextConstants } from "constants/TextConstants";
import { getAddressStatusIcon } from '../../common/AddressVerification' ;
import CustomTooltip from "common/Tooltip";
import ClubbingSuggestion from "./ClubbingSuggestion";

interface DriverAssignmentProps {
  open: boolean;
  consignment: any;
  closePopup: () => void;
}

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children?: ReactElement<any, any> },
  ref: Ref<unknown>
) {
  return <Slide direction="left" ref={ref} {...props} />;
});

const DriverAssignment = (props: DriverAssignmentProps) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const {
    consignmentDataAfterAddressUpdate,
    supportDriverDetails,
    supportDriverUpdateResult,
    driverUpdateResult,
    primaryDriverUpdateResult,
    clubbingSuggestion,
    clubbingLoading,
  } = useSelector((state: AppState) => state.expressDashboard);
  const { countryCode, clubbingConfigDetails } = useSelector((state: AppState) => state.common);
  
  // Check if clubbing is enabled for a given store
  const isClubbingEnabledForStore = useCallback((storeCode: string) => {
    return isStoreEnabledInConfig(storeCode, clubbingConfigDetails);
  }, [clubbingConfigDetails]);

  const { open, consignment: consignmentData, closePopup } = props;

  const getInitialState = () => ({
    consignment: consignmentData,
    supportDriversIds: [],
    isSupportDriverTabDisabled: true,
    supportDriverDetailsState: [],
    isCheckboxDisabled: false,
    addressStatusAfterUpdate: "",
    listToShow: [] as any[],
    tabValue: AppConstants.EXPRESS_CONSTANTS.DRIVER_TABS.PRIMARY_DRIVER,
  });
  
  const [state, setState] = useState(getInitialState);
  const [selectedConsignmentCodes, setSelectedConsignmentCodes] = useState<string[]>([]);

  const updateState = (updates: Partial<typeof state>) =>
    setState((prev) => ({ ...prev, ...updates }));

  // Extract delivery information
  const extractDeliveryInformation = useCallback((data: any) => {
    if (!data) return {};
    const deliveryInformation = data.deliveryInformation || {};
    const metadata = data.metadata || {};
    const custInfo = deliveryInformation.customerContactInfo || { address: {} };
    const deliveryTime = deliveryInformation.deliverySlot
      ? `${localTimeString(new Date(deliveryInformation.deliverySlot.endTime))}`
      : null;
    const items = data.products || [];
    return { deliveryInformation, metadata, custInfo, deliveryTime, items };
  }, []);

  const {
    deliveryInformation,
    metadata,
    custInfo,
    deliveryTime,
    items,
  } = useMemo(() => extractDeliveryInformation(state.consignment), [
    state.consignment,
    extractDeliveryInformation,
  ]);

const rtdBreached = consignmentData?.rtdBreached

  // Check if clubbing is enabled for current store
  const isClubbingEnabled = useMemo(() => {
    const currentStore = state?.consignment?.posInformation?.store;
    return isClubbingEnabledForStore(currentStore);
  }, [state?.consignment?.posInformation?.store, isClubbingEnabledForStore]);

  const { addressStatusAfterUpdate, isCheckboxDisabled } = useMemo(() => {
    if (!consignmentDataAfterAddressUpdate) {
      return { addressStatusAfterUpdate: "", isCheckboxDisabled: false };
    }
    const updatedDeliveryInfo = consignmentDataAfterAddressUpdate.deliveryInformation || {};
    const updatedCustomerInfo = updatedDeliveryInfo.customerContactInfo || {
      address: {},
    };
    const status = updatedCustomerInfo.address?.status || "";
    return {
      addressStatusAfterUpdate: status,
      isCheckboxDisabled: status === ADDRESS_STATUS.UNVERIFIED,
    };
  }, [consignmentDataAfterAddressUpdate]);

  const orderStatus = state?.consignment?.metadata?.status;  

  useEffect(() => {
    if (consignmentData) {
      updateState({ consignment: consignmentData });
    }
  }, [consignmentData]);

  useEffect(() => {
    const supportDriversIds = state.consignment?.supportDriversIds || [];
    updateState({ supportDriversIds });
  }, [state.consignment]);

  // Enable/disable support driver tab
  useEffect(() => {
    const driverId =
      state.consignment?.deliveryInformation?.driver?.driverId;
    updateState({ isSupportDriverTabDisabled: !driverId });
  }, [state.consignment]);

  // Update consignment and support drivers after assigning driver
  useEffect(() => {
    if (
      isNonEmptyObject(driverUpdateResult) &&
      driverUpdateResult?.consignmentCode === state.consignment.consignmentCode
    ) {
      const supportDriversIds = driverUpdateResult.supportDriversIds || [];
      updateState({ consignment: driverUpdateResult, supportDriversIds });
    }
  }, [driverUpdateResult, state.consignment.consignmentCode]);

  // Update consignment and support drivers after assigning support driver
  useEffect(() => {
    if (
      isNonEmptyObject(supportDriverUpdateResult) &&
      supportDriverUpdateResult?.consignmentCode === state.consignment.consignmentCode
    ) {
      const supportDriversIds =
        supportDriverUpdateResult.supportDriversIds || [];
      updateState({
        consignment: supportDriverUpdateResult,
        supportDriversIds,
      });
    }
  }, [supportDriverUpdateResult, state.consignment.consignmentCode]);

  // Update consignment after assigning primary driver updates 
  useEffect(() => {
    if (
      isNonEmptyObject(primaryDriverUpdateResult) &&
      primaryDriverUpdateResult?.consignmentCode === state.consignment.consignmentCode
    ) {
      updateState({
        consignment: primaryDriverUpdateResult,
        tabValue: AppConstants.EXPRESS_CONSTANTS.DRIVER_TABS.PRIMARY_DRIVER,
      });
    }
  }, [primaryDriverUpdateResult, state.consignment.consignmentCode]);

  // Update support driver details
  useEffect(() => {
    if (supportDriverDetails?.length) {
      updateState({ supportDriverDetailsState: supportDriverDetails });
    }
  }, [supportDriverDetails]);

  // Fetch support driver details
  useEffect(() => {
    if (state.supportDriversIds.length) {
      dispatch(fetchSupportDriverDetails({ payload: state.supportDriversIds }));
    } else {
      updateState({ supportDriverDetailsState: [] });
    }
  }, [dispatch, state.supportDriversIds]);

  const isOrderRestricted = (status: string) => 
    AppConstants.EXPRESS_CONSTANTS.CONSIGNMENTS_DATA.CLUBBED_RESTRICTED_STATUSES.includes(status);

  // Fetch clubbing suggestions when dialog opens
  useEffect(() => {
    if (state?.consignment?.consignmentCode && open && isClubbingEnabled && !clubbingLoading) {
      const orderStatus = state?.consignment?.metadata?.status;
      // Only fetch if order is NOT in restricted status
      if (isOrderRestricted(orderStatus)) return;

      const payload = {
        hubCode: state?.consignment?.posInformation?.store,
        consignmentCode:  state.consignment.consignmentCode,
      };
      dispatch(fetchClubbingSuggestion({ params: payload }));
    }
  }, [state.consignment, open, dispatch, isClubbingEnabled, clubbingLoading]);

  const dialogPaperProps = {
    classes: {
      root: classes.dialogPaperPropsRoot,
    },
    square: true,
    style: { overflow: "visible" } as React.CSSProperties,
  };

  const confirmDialogPaperProps = {
    classes: {
      root: classes.confirmDialogPaperPropsRoot,
    },
  };

  const getStatusField = (fieldName: string, statusCode: string) => {
    const statuses: any = AppConstants.EXPRESS_CONSTANTS.CONSIGNMENTS_DATA.STATUSES;
    return statusCode && statuses[statusCode]
      ? statuses[statusCode][fieldName]
      : "";
  };

  const handleDialogClose = useCallback(() => {
    closePopup();
    setState(getInitialState());
    setSelectedConsignmentCodes([]); // Reset selected consignment codes
    dispatch(clearClubbingSuggestion());
    dispatch(closeDialog());
  }, [closePopup, dispatch]);

  const removeSupportDriver = useCallback(
    (id: string) => {
      const payload = {
        consignmentCode: state.consignment.consignmentCode,
        driverIds: [id],
      };
      dispatch(releaseSupportDriver({ payload }));
    },
    [dispatch, state.consignment.consignmentCode]
  );

  const removePrimaryDriver = useCallback(
    (id: string) => {
      const payload = {
        consignmentCode: state.consignment.consignmentCode,
        driverIds: [id],
      };
      dispatch(releasePrimaryDriver({ payload }));
    },
    [dispatch, state.consignment.consignmentCode]
  );

  const shouldShowClubbingSuggestion = useCallback(() => {
    return (
      open &&
      isClubbingEnabled &&
      hasValidClubbingSuggestions(clubbingSuggestion, rtdBreached) &&
      state.tabValue === AppConstants.EXPRESS_CONSTANTS.DRIVER_TABS.PRIMARY_DRIVER
    );
  }, [open, isClubbingEnabled, clubbingSuggestion, state.tabValue, rtdBreached]);

  const handleSelectionChange = useCallback((selectedCodes: string[]) => {
    setSelectedConsignmentCodes(selectedCodes);
  }, []);

  const getTitleContent = () => {
    return (
      <Grid className={classes.titleContainer} container>
        <Grid className="headingContainer" container>
          <Grid container alignItems="center">
            <Typography className="titleHeading">
              <span className="label"> Driver Assignment </span>
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const assignedDriver = useMemo(() => {
    const driver = state.consignment?.deliveryInformation?.driver;
    if (!driver || !driver?.driverId) return []; // Ensure driver exists and has a driverId
    const { driverId, driverName, vehicleType } = driver;
    const { icon, alt } = getVehicleIcon(vehicleType);

    return [
      {
        id: driverId,
        name: driverName,
        icon: icon,
        alt: alt,
      },
    ];
  }, [state.consignment?.deliveryInformation?.driver]);

  const supportDrivers = useMemo(
    () =>
      state.supportDriverDetailsState?.map((driver: any) => {
        const { icon, alt } = getVehicleIcon(driver.vehicleType); // Use helper to get icon and alt
        return {
          id: driver.driverId,
          name: driver.driverName,
          icon,
          alt,
        };
      }),
    [state.supportDriverDetailsState]
  );
  const handleTabChange = useCallback(
    (event: React.ChangeEvent<{}>, value: string) => {
      updateState({ tabValue: value as TextConstants });
      setSelectedConsignmentCodes([]); // Reset selected consignment codes when switching tabs
    },
    []
  );

  const getDetailsContent = () => {
    return (
      <Grid className={classes.mycontentContainer}>
        <Grid className="detailsHeader" container>
          <Grid container justify="space-between">
            <Grid item style={{ display: "flex" }}>
              <Grid
                item
                className="deliveryDetails detailsContainer"
               >
                <span className="delivery">{TextConstants.CONSIGNMENTID} :</span>
                <span className="deliveryTimeDetails time">
                  {state.consignment.consignmentCode}
                </span>
              </Grid>
              {metadata && metadata.deliveryStatus && (
                <Grid
                  className="tagContainer"
                  item
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <Typography
                    className={[
                      "tag",
                      getStatusField(
                        "color",
                        metadata && metadata.deliveryStatus
                      ),
                    ].join(" ")}
                  >
                    {metadata && metadata.deliveryStatus
                      ? metadata.deliveryStatus
                      : ""}
                  </Typography>
                </Grid>
              )}
            </Grid>
            <Grid item style={{ display: "flex" }}>
              <Typography className="vehicleType">
                {deliveryInformation?.preferredVehicle || ""}
                Delivery
              </Typography>
               <img 
                src={getVehicleIcon(deliveryInformation?.preferredVehicle || "DEFAULT").icon} 
                alt={getVehicleIcon(deliveryInformation?.preferredVehicle || "DEFAULT").alt} 
              />
            </Grid>
          </Grid>
          <Grid container className="deliveryDetails" justify="space-between">
            <Grid item>
              <Typography className="delivery">Delivery Time</Typography>
            </Grid>
            <Grid item>
              <Grid container>
                <Typography className="deliveryTimeDetails">
                  {deliveryTime}
                </Typography>
                {deliveryInformation &&
                  deliveryInformation.deliverySlot &&
                  deliveryInformation.deliverySlot.promiseDeliveryTime && (
                    <Typography className="deliveryTimeDetails time">
                      (
                      {deliveryInformation.deliverySlot.promiseDeliveryTime +
                        " mins"}
                      )
                    </Typography>
                  )}
              </Grid>
            </Grid>
          </Grid>
          <Grid container className="deliveryDetails" justify="space-between">
            <Grid item>
              <Typography className="delivery">Total Items</Typography>
            </Grid>
            <Grid item>
              <Grid container justify="space-around">
                <Typography className="deliveryTimeDetails">
                  {items && items.length}
                </Typography>
                {deliveryInformation && deliveryInformation.totalWeight && (
                  <Typography className="deliveryTimeDetails time">
                    ({deliveryInformation.totalWeight + " kg"})
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid container className="deliveryDetails">
            <Typography className="deliveryTimeDetails">
              Customer Details
            </Typography>
          </Grid>
          <Grid container className="deliveryDetails">
            <Grid item>
              <Typography className="delivery">
                {custInfo && custInfo?.name}
              </Typography>
            </Grid>
            {addressStatusAfterUpdate ? (
              <Grid item style={{ marginLeft: "12px" }}>
                {addressStatusAfterUpdate === ADDRESS_STATUS.UNVERIFIED ? (
                  <CustomTooltip title="Address is Unverified">
                    <img src={unverifiedIcon} alt="info-icon" />
                  </CustomTooltip>
                  
                ) : (
                  ""
                )}
              </Grid>
            ) : (
              <Grid item style={{ marginLeft: "12px" }}>
                {getAddressStatusIcon(custInfo?.address?.status)}
              </Grid>
            )}
          </Grid>
          <Grid item className="deliveryDetails" style={{ display: "flex" }}>
            <img className="location" src={locationIcon} alt="location"></img>
            <Typography className="delivery">
              {getAddressInfo(
                custInfo && custInfo.address ? custInfo.address : ""
              )}
            </Typography>
          </Grid>
          <Grid
            container
            className="assignedDriverComponent"
            style={{ display: "block" }}
          >
            <Grid className="">
              <Typography className="driverHeading">{TextConstants.ASSIGNED_DRIVERS}</Typography>
            </Grid>
            <AssignedDriverChips
              title="Primary Drivers"
              drivers={assignedDriver}
              removeDriver={removePrimaryDriver}
              classes={classes}
              noDataMessage={TextConstants.NO_PRIMARY_DRIVER}
            />
            {supportDrivers.length > 0 && (
              <AssignedDriverChips
                title="Support Drivers"
                drivers={supportDrivers || []}
                removeDriver={removeSupportDriver}
                classes={classes}
                noDataMessage={TextConstants.NO_SUPPORT_DRIVER}
              />
            )}
          </Grid>
        </Grid>

        <TabContext value={state.tabValue}>
          <Tabs
            value={state.tabValue}
            className={classes.tabs}
            aria-label="Driver Tabs"
            onChange={handleTabChange}
          >
            <Tab
              className="tabLabel"
              label={AppConstants.EXPRESS_CONSTANTS.DRIVER_TABS.PRIMARY_DRIVER}
              value={AppConstants.EXPRESS_CONSTANTS.DRIVER_TABS.PRIMARY_DRIVER}
            />
            <Tab
              className={`tabLabel ${
                state.isSupportDriverTabDisabled && "disabledTab"
              }`}
              label={
                <span>
                  {AppConstants.EXPRESS_CONSTANTS.DRIVER_TABS.SUPPORT_DRIVER}
                </span>
              }
              value={AppConstants.EXPRESS_CONSTANTS.DRIVER_TABS.SUPPORT_DRIVER}
              disabled={state.isSupportDriverTabDisabled}
            />
            {state.isSupportDriverTabDisabled && (
              <Tooltip
                arrow
                title="Assign primary driver to enable support drivers"
              >
                <img
                  src={infoIcon}
                  alt="info-icon"
                  className={classes.infoIconStyle}
                />
              </Tooltip>
            )}
          </Tabs>
          <TabPanel
            className={classes.tabValues}
            value={AppConstants.EXPRESS_CONSTANTS.DRIVER_TABS.PRIMARY_DRIVER}
          >
            <PrimaryDriver
              consignment={state.consignment}
              countryCode={countryCode}
              clubbingSuggestions={clubbingSuggestion || {}}
              selectedConsignmentCodes={selectedConsignmentCodes}
              isClubbingEnabled={isClubbingEnabled}
              isOrderRestricted={isOrderRestricted(orderStatus)}
              rtdBreached={rtdBreached}
            />
          </TabPanel>

          <TabPanel
            className={classes.tabValues}
            value={AppConstants.EXPRESS_CONSTANTS.DRIVER_TABS.SUPPORT_DRIVER}
          >
            <SupportDriver
              consignment={state.consignment}
              countryCode={countryCode}
            />
          </TabPanel>
        </TabContext>
      </Grid>
    );
  };


  return (
    <>
      <CustomDialog
        open={open}
        TransitionComponent={Transition}
        PaperProps={dialogPaperProps}
        title={getTitleContent()}
        content={getDetailsContent()}
        actions={null}
        handleClose={handleDialogClose}
      ></CustomDialog>
      {shouldShowClubbingSuggestion() && (
        <ClubbingSuggestion 
          suggestionData={clubbingSuggestion} 
          onSelectionChange={handleSelectionChange}
        />
      )}
    </>
  );
};

export default DriverAssignment;
